import React, { useEffect, useMemo, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';

import { useQueryWithError } from '../graphql/graphqlUtils';
import { QuestionListQuery } from '../graphql/QuestionListQuery.graphql';
import type {
	QuestionListQuery as QuestionListQueryType,
	QuestionListQueryVariables as QuestionListQueryVariablesType,
} from '../graphql/__types__/QuestionListQuery';
import { ANSWER_CONTENT_TYPE } from '../contentType';
import { FilterType, getCQLForFilter } from '../QuestionsFilters';
import { QUESTION_NATIVE } from '@confluence/named-routes/entry-points/routes';

import { mapQuestionDefaultValues } from './questionListUtils';
import { SpaceQuestionsListMacro } from './SpaceQuestionsListMacro';
import { BaseQuestionsListMacro } from './BaseQuestionsListMacro';

type GlobalQuestionsListMacroProps = {
	limit: number;
	filter: string;
	space?: string;
	includeTopics?: 'all' | 'any';
	topics?: string[];
};

export const GlobalQuestionsListMacro: React.FC<GlobalQuestionsListMacroProps> = ({
	limit,
	filter,
	space,
	includeTopics = 'all',
	topics = [],
}) => {
	const { cloudId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [cql, setCql] = useState<string>('');

	const filterType: FilterType =
		filter === 'popular'
			? FilterType.POPULAR
			: filter === 'unanswered'
				? FilterType.UNANSWERED
				: FilterType.RECENT;

	const questionsLimit = limit || 10;

	const queryVariables = useMemo(
		() => ({
			cql,
			limit: questionsLimit,
			cloudId,
			answerContentType: ANSWER_CONTENT_TYPE,
		}),
		[cloudId, cql, questionsLimit],
	);

	const { data, loading, error } = useQueryWithError<
		QuestionListQueryType,
		QuestionListQueryVariablesType
	>(QuestionListQuery, {
		variables: queryVariables,
		skip: !cql,
	});

	const questions = useMemo(
		() =>
			data?.search?.nodes && data?.search?.nodes.length > 0
				? data?.search?.nodes.map((question) => mapQuestionDefaultValues(question?.content))
				: [],
		[data?.search?.nodes],
	);

	useEffect(() => {
		const generatedCql = getCQLForFilter(filterType, topics, includeTopics);
		setCql(generatedCql);
	}, [filterType, topics, includeTopics]);

	useEffect(() => {
		if (!loading && !error && data?.search) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'GlobalQuestionsListMacro',
				},
			}).fire();
		}
	}, [cql, loading, error, data?.search, createAnalyticsEvent]);

	if (space) {
		return (
			<SpaceQuestionsListMacro
				limit={limit}
				filter={filter}
				spaceKey={space}
				topics={topics}
				includeTopics={includeTopics}
			/>
		);
	}

	return (
		<BaseQuestionsListMacro
			questions={questions}
			loading={loading || !cql}
			error={error}
			limit={questionsLimit}
			routerName={QUESTION_NATIVE.name}
			defaultLimit={10}
		/>
	);
};
